import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { TrendingTopic } from '../../../api/dashboardApi';
import { BRAND_BLUE_SOFT, BRAND_ORANGE, BRAND_ORANGE_LIGHT } from '../../../design-system/chartColors';

interface TrendingTopicsPanelProps {
    isLoading: boolean;
    topics: TrendingTopic[];
    theme: string;
}

const TrendingTopicsPanel: React.FC<TrendingTopicsPanelProps> = ({ isLoading, topics, theme }) => {
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    // Clicking a trending topic opens it in the AI chat, pre-filled as a question.
    const openTopic = (label: string) => {
        navigate(`/app?q=${encodeURIComponent(label)}`);
    };

    const cardBase: React.CSSProperties = {
        padding: '18px 20px',
        borderRadius: 12,
        background: isDark ? '#101828' : 'var(--ds-surface)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#eaecf0'}`,
        boxShadow: isDark
            ? '0 4px 6px -1px rgba(0,0,0,0.5),0 2px 4px -1px rgba(0,0,0,0.4)'
            : '0 4px 6px -1px rgba(0,0,0,0.07),0 2px 4px -1px rgba(0,0,0,0.05),inset 0 1px 0 rgba(255,255,255,0.9)',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    };

    const grid: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))',
        gap: '16px',
    };

    // QA: organise trending topics across the intended domains rather than one
    // flat list. Each topic is classified by keyword; empty domains are hidden.
    const DOMAINS: { key: string; label: string; test: RegExp }[] = [
        { key: 'ai', label: 'Artificial Intelligence', test: /\b(ai|a\.i\.|artificial intelligence|machine learning|deep learning|generative|llm|deepfake|algorithm|neural)\b/i },
        { key: 'crypto', label: 'Crypto & Blockchain', test: /\b(crypto|cryptocurrenc|blockchain|bitcoin|web3|digital asset|stablecoin|token)\b/i },
        { key: 'cyber', label: 'Cybersecurity', test: /\b(cyber|ransomware|data breach|malware|hacking|phishing|security|privacy|surveillance|biometric)\b/i },
        { key: 'quantum', label: 'Quantum & Emerging Tech', test: /\b(quantum|semiconductor|chip|5g|iot|robotic|autonomous)\b/i },
        { key: 'other', label: 'Other Policy Topics', test: /.*/ },
    ];
    const classify = (label: string): string => (DOMAINS.find(d => d.test.test(label || '')) || DOMAINS[DOMAINS.length - 1]).key;
    // Preserve global rank (topics are pre-sorted by count desc).
    const ranked = topics.map((t, i) => ({ t, rank: i }));
    const grouped = DOMAINS
        .map(d => ({ ...d, items: ranked.filter(r => classify(r.t.label) === d.key) }))
        .filter(g => g.items.length > 0);

    const renderCard = (topic: TrendingTopic, i: number) => (
        <div
            key={i}
            style={cardBase}
            role="button"
            tabIndex={0}
            title="Ask this in chat"
            onClick={() => openTopic(topic.label)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTopic(topic.label); } }}
            onMouseEnter={e => {
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = isDark
                    ? '0 8px 16px -2px rgba(0,0,0,0.6),0 4px 8px -2px rgba(0,0,0,0.5)'
                    : '0 8px 16px -2px rgba(0,0,0,0.1),0 4px 8px -2px rgba(0,0,0,0.06),inset 0 1px 0 rgba(255,255,255,1)';
            }}
            onMouseLeave={e => {
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = cardBase.boxShadow as string;
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 26, height: 26, borderRadius: '50%',
                        background: isDark ? '#101828' : 'var(--ds-surface-2)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : '#d0d5dd'}`,
                        fontSize: '0.68rem', fontWeight: 700,
                        color: isDark ? '#98a2b3' : '#667085', flexShrink: 0,
                    }}>#{i + 1}</span>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                        style={{ color: isDark ? BRAND_BLUE_SOFT : BRAND_ORANGE_LIGHT, flexShrink: 0 }}>
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                </div>
                {topic.count > 1 && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        padding: '2px 8px', borderRadius: 99,
                        background: isDark ? 'rgba(164, 188, 253, 0.12)' : 'rgba(97,114,243,0.08)',
                        border: `1px solid ${isDark ? 'rgba(164, 188, 253, 0.25)' : 'rgba(97,114,243,0.2)'}`,
                        fontSize: '0.7rem', fontWeight: 600,
                        color: isDark ? BRAND_BLUE_SOFT : BRAND_ORANGE, whiteSpace: 'nowrap',
                    }}>
                        {topic.count} {topic.count === 1 ? 'ask' : 'asks'}
                    </span>
                )}
            </div>
            <p style={{
                margin: 0, fontSize: '0.9rem', fontWeight: 500,
                color: isDark ? '#eaecf0' : '#1d2939', lineHeight: 1.45,
                display: '-webkit-box', WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical' as const, overflow: 'hidden',
            }}>
                {topic.label}
            </p>
        </div>
    );

    return (
        <div style={{ padding: '1.5rem' }}>
            <h2 style={{ marginTop: 0, marginBottom: 4, fontSize: '1.4rem', fontWeight: 700, color: isDark ? '#f2f4f7' : '#1d2939' }}>
                Trending Topics
            </h2>
            <p style={{ marginTop: 4, marginBottom: 24, fontSize: '0.875rem', color: isDark ? '#98a2b3' : '#667085' }}>
                Top 10 topics most asked across all user conversations, grouped by theme.
            </p>

            {isLoading ? (
                <div style={grid}>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} style={{ height: 90, borderRadius: 12, background: isDark ? '#101828' : 'var(--ds-surface-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    ))}
                </div>
            ) : topics.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--ds-text-muted)', fontSize: '0.95rem' }}>
                    No trending topics yet. Start chatting to generate topics!
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    {grouped.map(group => (
                        <div key={group.key}>
                            <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem', fontWeight: 700, color: isDark ? '#c7d7fe' : BRAND_ORANGE, display: 'flex', alignItems: 'center', gap: 8 }}>
                                {group.label}
                                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--ds-text-muted)' }}>({group.items.length})</span>
                            </h3>
                            <div style={grid}>
                                {group.items.map(({ t, rank }) => renderCard(t, rank))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TrendingTopicsPanel;
